// 问题分析与回答生成算法

class QuestionAnalyzer {
  constructor(puzzle) {
    this.puzzle = puzzle;
    
    // 定义主题关键词映射
    this.themeKeywords = {
      time: {
        keywords: ['时间', '时钟', '手表', '挂钟', '钟', '表', '几点', '点钟'],
        indicators: ['时间', '时钟', '手表', '挂钟', '钟表']
      },
      illness: {
        keywords: ['病', '生病', '疾病', '症状', '患病', '病人'],
        indicators: ['病', '生病', '疾病', '症状', '患病']
      },
      death: {
        keywords: ['死', '死亡', '死了', '去世', '丧生', '身亡', '亡', '殒命'],
        indicators: ['死了', '死亡', '丧生', '遇害', '遇难', '殒命', '去世']
      },
      murder: {
        keywords: ['杀', '杀人', '谋杀', '凶手', '凶案', '杀害'],
        indicators: ['杀人', '谋杀', '凶手', '凶案', '杀害']
      },
      weather: {
        keywords: ['天气', '下雨', '暴雨', '雷', '雨', '雷雨', '打雷', '闪电'],
        indicators: ['下雨', '暴雨', '雷雨', '雨天']
      },
      military: {
        keywords: ['军队', '军人', '部队', '军官', '士兵', '军', '武器', '军事'],
        indicators: ['军队', '军人', '部队', '军官', '士兵', '军', '武器']
      },
      staff: {
        keywords: ['工作人员', '职员', '员工', '服务员', '侍者', '店员', '保安', '警卫', '管理员', '主管', '经理', '乘务员', '检票员', '司机', '驾驶员'],
        indicators: ['工作人员', '职员', '员工', '服务员', '侍者', '店员', '保安', '驾驶员', '司机', '乘务员']
      },
      vehicle: {
        keywords: ['车', '汽车', '火车', '飞机', '轮船', '船', '公交车', '巴士', '列车', '交通工具'],
        indicators: ['车', '汽车', '火车', '飞机', '轮船', '船', '列车', '交通工具']
      }
    };
    
    // 定义无关问题特征
    this.personalQuestions = [
      '你是', '您是', '你叫', '你的名字', '你几岁',
      '你男', '你女', '你多大', '你住', '性别',
      '你能', '你会', '你有', '你喜欢', '你觉得'
    ];
    
    // 常见问候语
    this.greetings = [
      '你好', '您好', '嗨', '喂', 'hi', 'hello',
      '早上好', '中午好', '下午好', '晚上好'
    ];
  }
  
  // 主要分析方法
  analyzeQuestion(question) {
    if (!question || question.length < 2) {
      return {
        answer: '不是',
        countQuestion: false
      };
    }

    // 清理文本
    const cleanText = question.trim().toLowerCase().replace(/[?？!！。.，,、\s]/g, '');

    // 检查是否是简单问候
    if (this.isSimpleGreeting(cleanText)) {
      return {
        answer: '不是',
        countQuestion: false
      };
    }

    // 检查是否是个人问题
    if (this.isPersonalQuestion(cleanText)) {
      return { 
        answer: '不是',
        countQuestion: false 
      };
    }
    
    // 检查问题是否包含否定词，用于处理正反问题的一致性
    const containsNegation = (text) => {
      const negationWords = ['不', '没', '无', '非', '别', '莫', '否'];
      return negationWords.some(word => text.includes(word));
    };
    
    // 提取问题的核心内容，移除否定词和疑问词
    const extractCore = (text) => {
      return text.replace(/不|没|无|非|别|莫|否|是否|有没有|是不是/g, '')
                .replace(/吗|呢|啊|哪|什么|怎么|如何|为什么/g, '');
    };
    
    const isNegated = containsNegation(cleanText);
    const questionCore = extractCore(cleanText);

    // 检查问题中是否包含双重否定
    const hasDoubleNegation = (text) => {
      const negationWords = ['不', '没', '无', '非', '别', '莫'];
      let count = 0;
      for(const word of negationWords) {
        const regex = new RegExp(word, 'g');
        const matches = text.match(regex);
        if(matches) {
          count += matches.length;
        }
      }
      return count >= 2;
    };

    // 主题检查
    for (const [theme, { keywords, indicators }] of Object.entries(this.themeKeywords)) {
      const containsThemeKeyword = keywords.some(keyword => questionCore.includes(keyword));
      if (!containsThemeKeyword) continue;

      // 检查主题是否在谜题内容中
      const puzzleIncludesTheme = indicators.some(indicator => 
        this.puzzle && this.puzzle.content && this.puzzle.content.includes(indicator)
      );

      // 处理双重否定
      const isDblNegation = hasDoubleNegation(cleanText);
      
      // 确保反向问题得到相反的答案
      const baseAnswer = puzzleIncludesTheme ? '是' : '不是';
      let finalAnswer = baseAnswer;
      
      // 如果问题包含奇数个否定词，答案取反
      if (isNegated && !isDblNegation) {
        finalAnswer = baseAnswer === '是' ? '不是' : '是';
      }

      // 确保每个主题对应的回答都只有"是"、"不是"或"是又不是"之一
      if (['death', 'murder', 'illness', 'weather', 'military', 'staff', 'vehicle'].includes(theme)) {
        return {
          answer: finalAnswer,
          countQuestion: puzzleIncludesTheme
        };
      }

      // 处理时间主题
      if (theme === 'time') {
        if (puzzleIncludesTheme) {
          if (cleanText.includes('几点') || cleanText.includes('点钟') || 
              cleanText.includes('问题') || cleanText.includes('坏了')) {
            return {
              answer: '是又不是',
              countQuestion: true
            };
          } else {
            return {
              answer: finalAnswer,
              countQuestion: true
            };
          }
        } else {
          return {
            answer: '不是',
            countQuestion: false
          };
        }
      }
    }

    // 简化默认回复逻辑
    if (cleanText.includes('是不是') || cleanText.includes('有没有') || 
        cleanText.includes('是否') || cleanText.match(/(.+)吗$/)) {
      // 提取问题中的关键词与谜题内容匹配
      const puzzleContent = this.puzzle.content.toLowerCase();
      const potentialKeywords = cleanText
        .replace(/是不是|有没有|是否|吗/g, '')
        .split(/[的地得了]/);
        
      const relevantKeyword = potentialKeywords.find(keyword => 
        keyword.length > 1 && puzzleContent.includes(keyword)
      );

      if (relevantKeyword) {
        // 同样处理否定情况
        return {
          answer: isNegated ? '不是' : '是',
          countQuestion: true
        };
      }
    }

    // 默认回复
    return {
      answer: '是又不是',
      countQuestion: false
    };
  }
  
  // 判断是否是简单问候
  isSimpleGreeting(text) {
    return this.greetings.some(greeting => 
      text === greeting || 
      text.startsWith(greeting)
    );
  }
  
  // 判断是否是针对AI自身的问题
  isPersonalQuestion(text) {
    return this.personalQuestions.some(phrase => text.includes(phrase));
  }
}

export default QuestionAnalyzer; 